import "zod/compile";
import { z } from "zod";
import { AgentChannelSchema } from "./harness/agentChannel";

/**
 * Typed memory rules — deterministic, org-scoped facts the agent harness
 * applies in code (they *bind* values). Free-text hints stay in the Memory
 * Bank; a rule is what a recurring human correction becomes once approved.
 *
 * Every kind has a normalized `matchKey` (derived by the backend from the
 * payload) so application is an exact lookup, never a similarity search.
 */
export const MemoryRuleKindSchema = z.enum([
  /** An extracted customer name or sender email domain that means a specific customer. */
  "customer_alias",
  /** A ship-to address that is one of *our* sites — never infer the customer from it. */
  "location_is_own_site",
  /** A ship-to address that resolves to a specific stored location (and its customer). */
  "ship_to_alias",
  /** Free text in the document ("Maritimo", "by boat") that means a shipping-method code. */
  "carrier_term_alias",
  /** Which carrier account number applies for a customer under a condition. */
  "carrier_account_rule",
  /** A per-field hint appended to the extraction schema description before OCR. */
  "extraction_hint",
  /**
   * A sender (address or domain) that routes to one of our organizations, and/or
   * free text the organization classifier reads about that sender. Lives in the
   * routing org; the target is a sibling org in the same tenant.
   */
  "sender_route",
]);
export type MemoryRuleKind = z.infer<typeof MemoryRuleKindSchema>;

export const MemoryRuleStatusSchema = z.enum(["draft", "active", "retired"]);
export type MemoryRuleStatus = z.infer<typeof MemoryRuleStatusSchema>;

export const MemoryRuleSourceSchema = z.enum([
  "proposal",
  "chat",
  "manual",
  "backfill",
  // Recorded straight from a person's correction (a remittance's payer).
  "correction",
  // Recorded from an automatic identification corroborated by hard evidence.
  "auto_match",
]);
export type MemoryRuleSource = z.infer<typeof MemoryRuleSourceSchema>;

/** Postal address as it appears in a rule payload (all parts optional; the fingerprint decides). */
export const MemoryRuleAddressSchema = z.object({
  street: z.string().nullable(),
  city: z.string().nullable(),
  postalCode: z.string().nullable(),
  country: z.string().nullable(),
});
export type MemoryRuleAddress = z.infer<typeof MemoryRuleAddressSchema>;

/** What an alias is matched against; each kind has its own normalizer in matchKeys. */
export const CustomerAliasKindSchema = z.enum([
  "name",
  "email_domain",
  // The payer string on a bank transaction ("JETRO CASH C 719…"), keyed
  // after the ACH noise and trace numbers are stripped.
  "bank_payer",
  // A full sender address. Narrower than email_domain: one sender domain
  // often pays for several accounts (sysco.com), one mailbox rarely does.
  "email_address",
]);
export type CustomerAliasKind = z.infer<typeof CustomerAliasKindSchema>;

export const CustomerAliasPayloadSchema = z.object({
  kind: z.literal("customer_alias"),
  aliasKind: CustomerAliasKindSchema,
  /** The extracted name or the bare email domain (no `@`). */
  alias: z.string().min(1).max(255),
  customerId: z.uuid(),
});

export const LocationIsOwnSitePayloadSchema = z.object({
  kind: z.literal("location_is_own_site"),
  /** Human label, e.g. "Videojet De Meern warehouse". */
  label: z.string().min(1).max(255),
  address: MemoryRuleAddressSchema,
});

export const ShipToAliasPayloadSchema = z.object({
  kind: z.literal("ship_to_alias"),
  address: MemoryRuleAddressSchema,
  locationId: z.uuid(),
  /** When set, the alias only applies to drafts already bound to this customer. */
  customerId: z.uuid().nullable(),
});

export const CarrierTermAliasPayloadSchema = z.object({
  kind: z.literal("carrier_term_alias"),
  /** Verbatim document text, e.g. "Maritimo". */
  term: z.string().min(1).max(255),
  /** Canonical shipping-method code from the org's option table, e.g. "OCEAN". */
  shippingMethodCode: z.string().min(1).max(64),
  customerId: z.uuid().nullable(),
});

export const FreightAppliesToSchema = z.enum(["zhuhai", "non_zhuhai", "all"]);
export type FreightAppliesTo = z.infer<typeof FreightAppliesToSchema>;

export const CarrierAccountRulePayloadSchema = z.object({
  kind: z.literal("carrier_account_rule"),
  customerId: z.uuid(),
  carrierName: z.string().min(1).max(64),
  accountNumber: z.string().min(1).max(64),
  condition: z.object({
    shippingMethodCode: z.string().max(64).nullable(),
    appliesTo: FreightAppliesToSchema.nullable(),
    incoterm: z.string().max(16).nullable(),
  }),
});

export const ExtractionHintPayloadSchema = z.object({
  kind: z.literal("extraction_hint"),
  /** Dotted path in the extraction schema, e.g. "shippingMethod" or "lineItems.quantity". */
  fieldPath: z.string().min(1).max(255),
  hint: z.string().min(1).max(300),
  /** Classification labels the hint applies to; null = every document type. */
  documentTypes: z.array(z.string().min(1)).nullable(),
  /** Sender email domain the hint applies to; null = every sender. */
  senderDomain: z.string().max(255).nullable(),
  /**
   * Customers the hint applies to; absent, null or empty = every customer.
   * A limited hint applies only when the document's customer is known before
   * extraction and is one of these.
   */
  customerIds: z.array(z.uuid()).max(50).nullable().optional(),
});

export const SenderRouteKindSchema = z.enum(["email_domain", "email_address"]);
export type SenderRouteKind = z.infer<typeof SenderRouteKindSchema>;

/** Longest note a sender route can carry into the classifier prompt. */
export const SENDER_ROUTE_NOTE_MAX_LENGTH = 500;

export const SenderRoutePayloadSchema = z
  .object({
    kind: z.literal("sender_route"),
    senderKind: SenderRouteKindSchema,
    /** The bare domain (no `@`) or the full sender address. */
    sender: z.string().min(1).max(255),
    /** The organization the sender's documents belong to; null = note-only. */
    targetOrganizationId: z.uuid().nullable(),
    /** Free text shown to the classifier model as a team note about this sender. */
    note: z.string().max(SENDER_ROUTE_NOTE_MAX_LENGTH).nullable(),
    /** Classification labels the route applies to; null = every document type. */
    documentTypes: z.array(z.string().min(1)).nullable(),
  })
  .refine((p) => p.targetOrganizationId !== null || !!p.note?.trim(), {
    message: "A sender route needs a target organization or a note",
    path: ["targetOrganizationId"],
  });

export const MemoryRulePayloadSchema = z.discriminatedUnion("kind", [
  CustomerAliasPayloadSchema,
  LocationIsOwnSitePayloadSchema,
  ShipToAliasPayloadSchema,
  CarrierTermAliasPayloadSchema,
  CarrierAccountRulePayloadSchema,
  ExtractionHintPayloadSchema,
  SenderRoutePayloadSchema,
]);
export type MemoryRulePayload = z.infer<typeof MemoryRulePayloadSchema>;
export type MemoryRulePayloadOf<K extends MemoryRuleKind> = Extract<
  MemoryRulePayload,
  { kind: K }
>;

/** Which pipeline stage applied a rule. */
export const MemoryRuleUsageStageSchema = z.enum([
  "resolve_draft_details",
  "extraction_schema",
  "field_resolver",
  "review_agent",
  // Picking the customer for a new cash receipt.
  "cash_receipt_customer",
  // The Identify Customer workflow step (remittances).
  "identify_customer",
  // The Classify Organization routing step (sender → org).
  "classify_organization",
  // Picking the customer for an ingested discount request group.
  "discount_request_customer",
]);
export type MemoryRuleUsageStage = z.infer<typeof MemoryRuleUsageStageSchema>;

/** What a rule did when it was applied. */
export const MemoryRuleUsageEffectSchema = z.enum([
  "bound",
  "suggested",
  "excluded",
  "hint_injected",
  "mismatch",
]);
export type MemoryRuleUsageEffect = z.infer<typeof MemoryRuleUsageEffectSchema>;

// ---------------------------------------------------------------------------
// Correction events — the raw human-correction signal the learning loop mines.
// ---------------------------------------------------------------------------

/**
 * The product area a correction, proposal or rule belongs to. A rule only
 * applies in its own module: a cash-receipt alias never binds a sales order.
 */
export const MemoryModuleSchema = z.enum(["oms", "cash_receipt", "remittance"]);
export type MemoryModule = z.infer<typeof MemoryModuleSchema>;

/**
 * The areas a proposal can be filed in: an area where something reads that
 * kind of memory. Sales orders accepts every kind (it was the only area until
 * the teach paths could pick one). Extraction hints are read by any
 * extraction step, from the area set on the step. A customer alias is read
 * by remittance customer matching (name and sender aliases) and by cash
 * receipts (bank payer strings only).
 */
export function memoryModulesForProposal(proposal: {
  kind: string;
  aliasKind?: string;
}): MemoryModule[] {
  if (proposal.kind === "extraction_hint") {
    return [...MemoryModuleSchema.options];
  }
  if (proposal.kind === "customer_alias") {
    return proposal.aliasKind === "bank_payer"
      ? ["oms", "cash_receipt"]
      : ["oms", "remittance"];
  }
  return ["oms"];
}

/**
 * One piece of identifying evidence on the corrected item. `value` is the
 * normalized key (the same normalizer lookup uses), `raw` is as stated.
 */
export const MatchSignalSchema = z.object({
  kind: z.string().min(1).max(64),
  value: z.string().min(1).max(500),
  raw: z.string().min(1).max(500),
});
export type MatchSignal = z.infer<typeof MatchSignalSchema>;

/** Canonical draft fields a correction can target (patch keys, not UI labels). */
export const CorrectionFieldSchema = z.enum([
  "customerId",
  "shipToLocationId",
  "billToLocationId",
  "selectedProductVariantId",
  "shippingMethod",
  "freightTerms",
  "freightCarrierInstructions",
  "shippingAccountNumber",
  "orderType",
  "warehouse",
  "currency",
  "purchaseOrderNumber",
  "requestDate",
  "orderDate",
  "quantity",
  "unitPrice",
  "other",
]);
export type CorrectionField = z.infer<typeof CorrectionFieldSchema>;

export const CorrectionActorKindSchema = z.enum(["human", "system", "agent"]);
export type CorrectionActorKind = z.infer<typeof CorrectionActorKindSchema>;

export const CorrectionSourceSchema = z.enum([
  "header_edit",
  "line_edit",
  "line_match",
  "mismatch_resolution",
  "approval_delta",
  "submit_delta",
  "backfill",
  // Outcomes of assistant (Nami) actions, mined like corrections: a preview
  // the user discarded, a direct action they undid, and a hand edit made
  // shortly after an assistant change on the same record.
  "nami_discard",
  "nami_undo",
  "nami_followup_edit",
  // A person picked a different customer on a cash receipt or remittance.
  "customer_override",
]);
export type CorrectionSource = z.infer<typeof CorrectionSourceSchema>;

/** Snapshot of the extraction context at correction time, for clustering. */
/** Longest reason a person can give for a correction. */
export const CORRECTION_NOTE_MAX_LENGTH = 500;

export const CorrectionContextSchema = z.object({
  possibleNames: z.array(z.string()).nullable(),
  emailDomains: z.array(z.string()).nullable(),
  senderDomain: z.string().nullable(),
  destinationAddress: MemoryRuleAddressSchema.nullable(),
  /** Backend-derived fingerprint of `destinationAddress`. */
  addressFingerprint: z.string().nullable(),
  /** Verbatim extracted shipping-method / carrier text. */
  extractedShippingText: z.string().nullable(),
  /** How the corrected value had been bound: location match, identity match, or a rule. */
  bindingSource: z.enum(["location", "identity", "rule", "unknown"]).nullable(),
  workflowRunId: z.uuid().nullable(),
  stepRunId: z.uuid().nullable(),
  aiReasoningAuditId: z.uuid().nullable(),
  /** Set when the correction was made through Nami; optional so historical rows still validate. */
  via: AgentChannelSchema.nullable().optional(),
  /**
   * The corrected item's identifying evidence, built by its module's one
   * signals function (shared by logging and lookup so the keys match).
   * Sales-order drafts use the named fields above instead.
   */
  signals: z.array(MatchSignalSchema).max(20).optional(),
  /**
   * The person marked this change a one-off: it stays in the log but is never
   * learned from. Set after the fact from the prompt that follows a change.
   */
  oneOff: z.boolean().optional(),
  /** Why the person made the change, for reviewers only; never read by clustering. */
  note: z.string().max(CORRECTION_NOTE_MAX_LENGTH).nullable().optional(),
});
export type CorrectionContext = z.infer<typeof CorrectionContextSchema>;

// ---------------------------------------------------------------------------
// Proposals — what the learning loop or the teach chat asks a human to approve.
// ---------------------------------------------------------------------------

export const MemoryProposalStatusSchema = z.enum([
  "proposed",
  "accepted",
  "rejected",
  "superseded",
  "stale",
]);
export type MemoryProposalStatus = z.infer<typeof MemoryProposalStatusSchema>;

export const MemoryProposalSourceSchema = z.enum([
  "clustering",
  "chat",
  "manual",
]);
export type MemoryProposalSource = z.infer<typeof MemoryProposalSourceSchema>;

/** A free-text Memory Bank note proposed for approval (not a typed rule). */
export const MemoryNoteProposalPayloadSchema = z.object({
  kind: z.literal("note"),
  note: z.string().min(1).max(10_000),
  customerId: z.uuid().nullable(),
  customerEmailDomain: z.string().max(255).nullable(),
});

/** A runtime skill drafted from clustered chat questions. */
export const SkillDraftProposalPayloadSchema = z.object({
  kind: z.literal("skill_draft"),
  moduleKey: z.string().min(1).max(64),
  title: z.string().min(1).max(255),
  whenToUse: z.string().min(1).max(500),
  bodyMarkdown: z.string().min(1).max(20_000),
});

/** Everything a proposal can carry: every rule kind plus the two proposal-only kinds. */
export const MemoryProposalPayloadSchema = z.discriminatedUnion("kind", [
  CustomerAliasPayloadSchema,
  LocationIsOwnSitePayloadSchema,
  ShipToAliasPayloadSchema,
  CarrierTermAliasPayloadSchema,
  CarrierAccountRulePayloadSchema,
  ExtractionHintPayloadSchema,
  SenderRoutePayloadSchema,
  MemoryNoteProposalPayloadSchema,
  SkillDraftProposalPayloadSchema,
]);
export type MemoryProposalPayload = z.infer<typeof MemoryProposalPayloadSchema>;
export const MemoryProposalKindSchema = z.enum([
  ...MemoryRuleKindSchema.options,
  "note",
  "skill_draft",
]);
export type MemoryProposalKind = z.infer<typeof MemoryProposalKindSchema>;

export function isMemoryRuleKind(kind: string): kind is MemoryRuleKind {
  return (MemoryRuleKindSchema.options as readonly string[]).includes(kind);
}

/** Narrows a proposal payload to the rule payloads (excludes `note` and `skill_draft`). */
export function isMemoryRulePayload(
  payload: MemoryProposalPayload,
): payload is MemoryRulePayload {
  return isMemoryRuleKind(payload.kind);
}
